import axios, { AxiosInstance } from 'axios';

import { Configuration } from 'src/Services/API/generated';

export enum ForecastLineSource {
  AUTO = 'AUTO',
  MANUAL_REAL = 'MANUAL_REAL',
  MANUAL_FORECAST = 'MANUAL_FORECAST',
}

export interface ForecastViewOccurrenceDto {
  date: string;
  amount: number;
  isMaterialized: boolean;
  templateExpenseId?: string | null;
  hasCustomAmount?: boolean;
}

export interface ForecastViewPayerDto {
  id: string;
  firstName?: string;
  lastName?: string;
}

export interface ForecastViewBankAccountDto {
  id: string;
  name: string;
}

export enum CategoryNature {
  CHARGE = 'CHARGE',
  PRODUCT = 'PRODUCT',
}

export interface ForecastViewLineDto {
  id: string;
  source: ForecastLineSource;
  title: string;
  mainCategory: string;
  subCategory?: string | null;
  nature: CategoryNature;
  expenseType?: string | null;
  isIncome: boolean;
  paidBy?: ForecastViewPayerDto | null;
  paidByBankAccount?: ForecastViewBankAccountDto | null;
  occurrenceCount: number;
  unitAmount?: number | null;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  budgetRealizedAmount?: number | null;
  budgetOverrunAmount?: number | null;
  paidOccurrenceCount: number;
  pendingOccurrenceCount: number;
  occurrences: ForecastViewOccurrenceDto[];
  /** MANUAL_REAL lines only: their id is a real expense id, so they can be excluded. */
  isExcludable: boolean;
}

export interface ForecastExcludedLineDto {
  id: string;
  exclusionId: string;
  title: string;
  amount: number;
  mainCategory: string;
  subCategory?: string | null;
  date: string;
}

export interface BudgetForecastViewDto {
  year: number;
  autoBundleCount: number;
  autoBundleAmount: number;
  lines: ForecastViewLineDto[];
  realizedPaid: number;
  identifiedPending: number;
  cap?: number | null;
  remainingToCommit: number;
  productsSubtotal: number;
  chargesSubtotal: number;
  balance: number;
  excludedLines: ForecastExcludedLineDto[];
}

export interface BudgetForecastExclusionDto {
  id: string;
  expenseId: string;
  budgetYear: number;
  excludedById: string;
  createdAt: string;
}

export class BudgetForecastViewApi {
  private axiosInstance: AxiosInstance;

  constructor(configuration: Configuration) {
    this.axiosInstance = axios.create({
      baseURL: configuration.basePath ?? '',
      ...configuration.baseOptions,
    });
  }

  async getView(assetId: string, year: number): Promise<BudgetForecastViewDto> {
    const response = await this.axiosInstance.get<BudgetForecastViewDto>(
      `/assets/${assetId}/budget-forecast/view`,
      { params: { year } }
    );
    return response.data;
  }

  async excludeOperation(
    assetId: string,
    expenseId: string,
    budgetYear: number
  ): Promise<BudgetForecastExclusionDto> {
    const response = await this.axiosInstance.post<BudgetForecastExclusionDto>(
      `/assets/${assetId}/budget-forecast/exclusions`,
      { expenseId, budgetYear }
    );
    return response.data;
  }

  async reincludeOperation(assetId: string, exclusionId: string): Promise<void> {
    await this.axiosInstance.delete(`/assets/${assetId}/budget-forecast/exclusions/${exclusionId}`);
  }

  async setExclusionsInBulk(
    assetId: string,
    expenseIds: string[],
    budgetYear: number,
    excluded: boolean
  ): Promise<void> {
    await this.axiosInstance.post(`/assets/${assetId}/budget-forecast/exclusions/bulk`, {
      expenseIds,
      budgetYear,
      excluded,
    });
  }
}
